/**
 * CTRL_07 Plugin - Bias & Fairness Testing
 * Custom UI with bias metrics visualization and fairness dashboard
 * Extends GenericConfigPanel with interactive bias analysis
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Progress,
  Alert,
  Statistic,
  Divider,
  Space,
  Tooltip,
  Tag,
  Table,
} from 'antd';
import {
  ExperimentOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors, spacing } from '../../../../theme/designTokens';

/**
 * Protected Attribute Card
 */
function ProtectedAttributeCard({ attribute, enabled, metrics, onToggle }) {
  const attributeInfo = {
    gender: { icon: '⚧️', name: 'Gender', example: 'Male, Female, Non-binary' },
    race: { icon: '🌍', name: 'Race/Ethnicity', example: 'Various ethnicities' },
    age: { icon: '🎂', name: 'Age', example: '18-25, 26-40, 41+' },
    disability: { icon: '♿', name: 'Disability Status', example: 'Disabled, Non-disabled' },
    religion: { icon: '⛪', name: 'Religion', example: 'Various beliefs' },
    ethnicity: { icon: '🌐', name: 'Ethnicity', example: 'Various origins' },
  };

  const info = attributeInfo[attribute] || { icon: '📊', name: attribute, example: '' };
  const biasScore = metrics?.bias_score || Math.random() * 0.3;
  const status = biasScore < 0.1 ? 'pass' : biasScore < 0.2 ? 'warning' : 'fail';
  const statusColors = {
    pass: colors.status.pass,
    warning: colors.severity.medium,
    fail: colors.status.fail,
  };

  return (
    <Card
      size="small"
      hoverable
      onClick={onToggle}
      style={{
        borderColor: enabled ? statusColors[status] : '#d9d9d9',
        backgroundColor: enabled ? (status === 'pass' ? '#f6ffed' : '#fff7e6') : '#fafafa',
        cursor: 'pointer',
      }}
      bodyStyle={{ padding: spacing.md }}
    >
      <Row align="middle" gutter={spacing.sm}>
        <Col flex="none">
          <div style={{ fontSize: 32 }}>{info.icon}</div>
        </Col>
        <Col flex="auto">
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {info.name}
            {enabled && (
              <CheckCircleOutlined
                style={{ color: colors.status.pass, marginLeft: spacing.xs }}
              />
            )}
          </div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>{info.example}</div>
          {enabled && (
            <div>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Bias Score</div>
              <Progress
                percent={(biasScore * 100).toFixed(0)}
                size="small"
                strokeColor={statusColors[status]}
                format={(percent) => `${(percent / 100).toFixed(2)}`}
              />
            </div>
          )}
        </Col>
      </Row>
    </Card>
  );
}

/**
 * Fairness Metrics Table
 */
function FairnessMetricsTable({ selectedMetrics }) {
  // Mock fairness test results
  const mockResults = [
    {
      key: 'demographic_parity',
      metric: 'Demographic Parity',
      description: 'Equal selection rates across groups',
      score: 0.92,
      threshold: 0.8,
      status: 'pass',
    },
    {
      key: 'equalized_odds',
      metric: 'Equalized Odds',
      description: 'Equal TPR/FPR across groups',
      score: 0.85,
      threshold: 0.8,
      status: 'pass',
    },
    {
      key: 'disparate_impact',
      metric: 'Disparate Impact (80% Rule)',
      description: 'Ratio >= 0.8',
      score: 0.78,
      threshold: 0.8,
      status: 'warning',
    },
    {
      key: 'equal_opportunity',
      metric: 'Equal Opportunity',
      description: 'Equal TPR across groups',
      score: 0.88,
      threshold: 0.8,
      status: 'pass',
    },
  ].filter((m) => selectedMetrics?.includes(m.key));

  const columns = [
    {
      title: 'Metric',
      dataIndex: 'metric',
      key: 'metric',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{text}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{record.description}</div>
        </div>
      ),
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      width: 150,
      render: (score) => (
        <Progress
          percent={(score * 100).toFixed(0)}
          size="small"
          format={(percent) => (percent / 100).toFixed(2)}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const config = {
          pass: { color: 'success', icon: <CheckCircleOutlined />, text: 'Pass' },
          warning: { color: 'warning', icon: <WarningOutlined />, text: 'Warning' },
          fail: { color: 'error', icon: <CloseCircleOutlined />, text: 'Fail' },
        };
        const c = config[status];
        return (
          <Tag color={c.color} icon={c.icon}>
            {c.text}
          </Tag>
        );
      },
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={mockResults}
      pagination={false}
      size="small"
      bordered
    />
  );
}

/**
 * Bias Testing Stats Overview
 */
function BiasStatsOverview({ config }) {
  const selectedAttributes = config?.protected_attributes || [];
  const selectedMetrics = config?.fairness_metrics || [];
  const biasThreshold = config?.bias_threshold || 0.2;

  // Mock stats
  const testsRun = 24;
  const testsPassed = 18;
  const testsWarning = 4;
  const testsFailed = 2;
  const passRate = ((testsPassed / testsRun) * 100).toFixed(0);

  return (
    <Row gutter={[spacing.md, spacing.md]}>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Protected Attributes"
            value={selectedAttributes.length}
            suffix="/ 6"
            prefix={<ExperimentOutlined style={{ color: colors.primary }} />}
            valueStyle={{ color: colors.primary }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Fairness Metrics"
            value={selectedMetrics.length}
            suffix="/ 4"
            prefix={<InfoCircleOutlined style={{ color: colors.primary }} />}
            valueStyle={{ color: colors.primary }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Tests Passed"
            value={passRate}
            suffix="%"
            prefix={<CheckCircleOutlined style={{ color: colors.status.pass }} />}
            valueStyle={{ color: colors.status.pass }}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            {testsPassed} passed, {testsWarning} warnings, {testsFailed} failed
          </div>
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Bias Threshold"
            value={biasThreshold.toFixed(2)}
            prefix={<WarningOutlined style={{ color: colors.severity.medium }} />}
            valueStyle={{ color: colors.severity.medium }}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            Max acceptable bias
          </div>
        </Card>
      </Col>
    </Row>
  );
}

/**
 * CTRL_07 Plugin Component
 */
function CTRL_07_Plugin({ projectId, controlCode }) {
  const [showDashboard, setShowDashboard] = useState(true);
  const [config, setConfig] = useState(null);

  // Listen for config changes from GenericConfigPanel
  useEffect(() => {
    // In production, this would fetch actual config
    // For demo, using mock data
    setConfig({
      enabled: false,
      fairness_metrics: ['demographic_parity', 'disparate_impact'],
      protected_attributes: ['gender', 'race', 'age'],
      bias_threshold: 0.2,
      test_frequency: 'quarterly',
    });
  }, [projectId, controlCode]);

  return (
    <div>
      {showDashboard && config && (
        <>
          <Alert
            message="Bias & Fairness Testing Dashboard"
            description="Enhanced visualization for fairness testing configuration. This plugin extends the standard schema-driven config with visual attribute selection and bias metrics analysis."
            type="info"
            showIcon
            closable
            onClose={() => setShowDashboard(false)}
            style={{ marginBottom: spacing.lg }}
          />

          <Card
            title={
              <Space>
                <ExperimentOutlined />
                Fairness Testing Overview
              </Space>
            }
            style={{ marginBottom: spacing.lg }}
          >
            <BiasStatsOverview config={config} />
          </Card>

          <Divider orientation="left">Protected Attributes Under Test</Divider>

          <Alert
            message="Interactive Attribute Cards"
            description="Click cards to toggle protected attributes for bias testing. Bias scores shown are from latest fairness tests."
            type="success"
            showIcon
            style={{ marginBottom: spacing.md }}
          />

          <Row gutter={[spacing.md, spacing.md]} style={{ marginBottom: spacing.lg }}>
            {['gender', 'race', 'age', 'ethnicity', 'disability', 'religion'].map((attr) => (
              <Col xs={24} sm={12} md={8} key={attr}>
                <ProtectedAttributeCard
                  attribute={attr}
                  enabled={config.protected_attributes?.includes(attr)}
                  metrics={{ bias_score: Math.random() * 0.3 }}
                  onToggle={() => {
                    // In production, this would update config via API
                    console.log(`Toggle ${attr}`);
                  }}
                />
              </Col>
            ))}
          </Row>

          <Divider orientation="left">Latest Fairness Test Results</Divider>

          <Card style={{ marginBottom: spacing.lg }}>
            <FairnessMetricsTable selectedMetrics={config.fairness_metrics} />
            <Alert
              message="Test Methodology"
              description="Fairness metrics are calculated across all protected attributes using industry-standard methods (Demographic Parity, Equalized Odds, Disparate Impact, Equal Opportunity)."
              type="info"
              showIcon
              style={{ marginTop: spacing.md }}
            />
          </Card>

          <Divider />
        </>
      )}

      {/* Standard GenericConfigPanel for actual configuration */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_07_Plugin;
